import { Fragment, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { LastUpdated } from "@/components/site/LastUpdated";
import { Link } from "@tanstack/react-router";
import { WeightLossOptionsButtons } from "@/components/site/WeightLossOptionsButtons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import type { RevisionGalleryImage, RevisionSpoke } from "@/lib/revision-spokes";
import { MedicalReviewBanner } from "./MedicalReview";
import { TelehealthCta } from "@/components/site/TelehealthCta";
import { SectionCta } from "@/components/site/SectionCta";
import bypassResectionEn from "@/assets/bypass-revision-resection-en.png.asset.json";
import bypassResectionEn480 from "@/assets/bypass-revision-resection-en-480.webp.asset.json";
import bypassResectionEn800 from "@/assets/bypass-revision-resection-en-800.webp.asset.json";
import bypassResectionEn1200 from "@/assets/bypass-revision-resection-en-1200.webp.asset.json";
import lapBandRemovedWebp800 from "@/assets/lap-band-removed-800.webp.asset.json";
import lapBandRemovedWebp1200 from "@/assets/lap-band-removed-1200.webp.asset.json";
import lapBandRemovedWebp1600 from "@/assets/lap-band-removed-1600.webp.asset.json";
import lapBandRemovedJpg800 from "@/assets/lap-band-removed-800.jpg.asset.json";
import lapBandRemovedJpg1200 from "@/assets/lap-band-removed-1200.jpg.asset.json";
import lapBandRemovedJpg1600 from "@/assets/lap-band-removed-1600.jpg.asset.json";
import bandTubingWebp800 from "@/assets/band-tubing-800.webp.asset.json";
import bandTubingWebp1200 from "@/assets/band-tubing-1200.webp.asset.json";
import bandTubingWebp1600 from "@/assets/band-tubing-1600.webp.asset.json";
import bandTubingJpg800 from "@/assets/band-tubing-800.jpg.asset.json";
import bandTubingJpg1200 from "@/assets/band-tubing-1200.jpg.asset.json";
import bandTubingJpg1600 from "@/assets/band-tubing-1600.jpg.asset.json";
import pouchContrastWebp800 from "@/assets/dilated-pouch-contrast-800.webp.asset.json";
import pouchContrastWebp980 from "@/assets/dilated-pouch-contrast-980.webp.asset.json";
import pouchContrastJpg800 from "@/assets/dilated-pouch-contrast-800.jpg.asset.json";
import pouchContrastJpg980 from "@/assets/dilated-pouch-contrast-980.jpg.asset.json";
import pouchAnnotatedWebp480 from "@/assets/dilated-pouch-boxed-en-480.webp.asset.json";
import pouchAnnotatedWebp800 from "@/assets/dilated-pouch-boxed-en-800.webp.asset.json";
import pouchAnnotatedWebp980 from "@/assets/dilated-pouch-boxed-en-980.webp.asset.json";
import pouchAnnotatedJpg480 from "@/assets/dilated-pouch-boxed-en-480.jpg.asset.json";
import pouchAnnotatedJpg800 from "@/assets/dilated-pouch-boxed-en-800.jpg.asset.json";
import pouchAnnotatedJpg980 from "@/assets/dilated-pouch-boxed-en-980.jpg.asset.json";
import { ProcedureFigure } from "@/components/site/ProcedureFigure";
import { ImageLightbox } from "@/components/site/ImageLightbox";
import { revisionGalleryResponsive } from "@/lib/service-responsive";

/**
 * Renders inline markdown-style links inside body copy, e.g.
 * "…[type 2 diabetes](/resources/obesity-and-diabetes)…".
 */
function renderInlineLinks(text: string) {
  const pattern = /\[([^\]]+)\]\((\/[^)]*)\)/g;
  const nodes: Array<string | ReactNode> = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    const target = match[2];
    const hashIndex = target.indexOf("#");
    const to = hashIndex >= 0 ? target.slice(0, hashIndex) : target;
    const hash = hashIndex >= 0 ? target.slice(hashIndex + 1) : undefined;
    nodes.push(
      <Link
        key={`${match[2]}-${match.index}`}
        to={to}
        hash={hash}
        className="underline underline-offset-2 hover:text-foreground"
      >
        {match[1]}
      </Link>,
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex === 0) return text;
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

function GalleryThumb({
  image,
  onOpen,
}: {
  image: RevisionGalleryImage;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group relative block w-full overflow-hidden rounded-lg border border-border text-left focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      aria-label={`Enlarge image: ${image.caption}`}
    >
      <img
        src={revisionGalleryResponsive[image.src]?.src ?? image.src}
        srcSet={revisionGalleryResponsive[image.src]?.srcSet}
        alt={image.alt}
        width={image.width}
        height={image.height}
        loading="lazy"
        decoding="async"
        sizes="(min-width: 640px) 30vw, calc((100vw - 3rem) / 3)"
        className="w-full object-cover transition-transform duration-300 group-hover:scale-105"
      />
      <span className="pointer-events-none absolute bottom-2 right-2 rounded-full bg-black/60 p-1.5 text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="15 3 21 3 21 9" />
          <polyline points="9 21 3 21 3 15" />
          <line x1="21" y1="3" x2="14" y2="10" />
          <line x1="3" y1="21" x2="10" y2="14" />
        </svg>
      </span>
    </button>
  );
}

function GalleryLightbox({
  images,
  index,
  onIndexChange,
  onClose,
}: {
  images: RevisionGalleryImage[];
  index: number | null;
  onIndexChange: (next: number) => void;
  onClose: () => void;
}) {
  const touchStart = useRef<{ x: number; y: number; t: number } | null>(null);
  const figureRef = useRef<HTMLElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const open = index !== null;
  const current = open ? images[index] : null;
  const many = images.length > 1;
  const canFullscreen = typeof document !== "undefined" && !!document.fullscreenEnabled;

  const step = useCallback(
    (delta: number) => {
      if (index === null) return;
      onIndexChange((index + delta + images.length) % images.length);
    },
    [index, images.length, onIndexChange],
  );

  const toggleFullscreen = useCallback(async () => {
    if (!figureRef.current) return;
    try {
      if (!document.fullscreenElement) {
        await figureRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch {
      // Ignore browsers that block the request or do not support it.
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowRight") step(1);
      if (event.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, step]);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  useEffect(() => {
    if (index === null || images.length < 2) return;
    const neighbors = [
      images[(index + 1) % images.length],
      images[(index - 1 + images.length) % images.length],
    ];
    for (const image of neighbors) {
      if (!image) continue;
      const preloader = new Image();
      preloader.decoding = "async";
      preloader.src = image.src;
    }
  }, [index, images]);

  if (!current) return null;

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? null : onClose())}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-4xl border-none bg-transparent p-0 shadow-none sm:w-full">
        <DialogTitle className="sr-only">{current.caption}</DialogTitle>
        <DialogDescription className="sr-only">
          Expanded view of {current.alt}
          {many ? ". Swipe or tap the image to see the next photo." : ""}
        </DialogDescription>
        <figure
          ref={figureRef}
          className={`relative flex select-none flex-col rounded-lg bg-black p-1 ${
            isFullscreen ? "h-screen w-screen" : "max-h-[calc(100svh-2rem)]"
          }`}
          style={{
            paddingLeft: "max(0.25rem, env(safe-area-inset-left))",
            paddingRight: "max(0.25rem, env(safe-area-inset-right))",
            paddingBottom: "max(0.25rem, env(safe-area-inset-bottom))",
            paddingTop: "max(0.25rem, env(safe-area-inset-top))",
          }}
        >
          {canFullscreen ? (
            <button
              type="button"
              onClick={toggleFullscreen}
              aria-label={isFullscreen ? "Exit full screen" : "Enter full screen"}
              className="absolute left-2 top-2 z-10 flex min-h-11 min-w-11 items-center justify-center rounded-full bg-black/60 text-white sm:hidden"
            >
              {isFullscreen ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M8 3v3a2 2 0 0 1-2 2H3" />
                  <path d="M21 8h-3a2 2 0 0 1-2-2V3" />
                  <path d="M3 16h3a2 2 0 0 1 2 2v3" />
                  <path d="M16 21v-3a2 2 0 0 1 2-2h3" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M3 7V5a2 2 0 0 1 2-2h2" />
                  <path d="M17 3h2a2 2 0 0 1 2 2v2" />
                  <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
                  <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
                </svg>
              )}
            </button>
          ) : null}
          <img
            src={current.src}
            alt={current.alt}
            width={current.width}
            height={current.height}
            loading="eager"
            decoding="async"
            draggable={false}
            className={`mx-auto w-full min-h-0 flex-1 touch-pan-y rounded-md object-contain ${
              isFullscreen ? "max-h-[calc(100vh-9rem)]" : "max-h-[calc(100svh-9rem)]"
            }`}
            onClick={() => many && step(1)}
            onTouchStart={(event) => {
              const touch = event.changedTouches[0];
              touchStart.current = { x: touch.clientX, y: touch.clientY, t: Date.now() };
            }}
            onTouchEnd={(event) => {
              const start = touchStart.current;
              touchStart.current = null;
              if (!start || !many) return;
              const touch = event.changedTouches[0];
              const dx = touch.clientX - start.x;
              const dy = touch.clientY - start.y;
              if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
                step(dx < 0 ? 1 : -1);
              }
            }}
          />
          {many ? (
            <>
              <button
                type="button"
                onClick={() => step(-1)}
                aria-label="Previous image"
                className="absolute left-2 top-1/2 hidden min-h-11 min-w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white sm:flex"
              >
                <span aria-hidden="true">‹</span>
              </button>
              <button
                type="button"
                onClick={() => step(1)}
                aria-label="Next image"
                className="absolute right-2 top-1/2 hidden min-h-11 min-w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white sm:flex"
              >
                <span aria-hidden="true">›</span>
              </button>
              <div
                role="tablist"
                aria-label="Image position"
                className="mt-3 flex items-center justify-center gap-2"
              >
                {images.map((image, i) => (
                  <button
                    key={image.src}
                    type="button"
                    role="tab"
                    aria-selected={i === index}
                    aria-label={`Go to image ${i + 1} of ${images.length}: ${image.caption}`}
                    onClick={() => onIndexChange(i)}
                    className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-white/15 ${
                      i === index ? "bg-white/20" : ""
                    }`}
                  >
                    <span
                      className={`block h-2.5 w-2.5 rounded-full ${
                        i === index ? "bg-white" : "bg-white/40"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </>
          ) : null}
          <figcaption className="mt-2 px-2 pb-2 text-center text-sm text-white">
            {current.caption}
            {many ? (
              <span className="mt-1 block text-white/70">
                {(index ?? 0) + 1} of {images.length}
              </span>
            ) : null}
          </figcaption>
        </figure>
      </DialogContent>
    </Dialog>
  );
}

type RevisionGallery = NonNullable<RevisionSpoke["sections"][number]["gallery"]>;

function GallerySection({ gallery }: { gallery: RevisionGallery }) {
  const [index, setIndex] = useState<number | null>(null);

  return (
    <figure className="mt-5" aria-label={gallery.label} role="group">
      <div className="grid grid-cols-3 items-start gap-3">
        {gallery.images.map((image, i) => (
          <figure key={image.src} className="m-0">
            <GalleryThumb image={image} onOpen={() => setIndex(i)} />
            <figcaption className="mt-1 line-clamp-2 text-xs text-foreground/70 md:mt-2 md:line-clamp-none md:text-sm">
              {image.caption}
            </figcaption>
          </figure>
        ))}
      </div>
      <figcaption className="mt-2 text-sm italic text-foreground/70">
        {gallery.caption}
      </figcaption>
      <GalleryLightbox
        images={[...gallery.images]}
        index={index}
        onIndexChange={setIndex}
        onClose={() => setIndex(null)}
      />
    </figure>
  );
}

function BypassRevisionFigure() {
  return (
    <figure className="mt-6" aria-label="Laparoscopic gastric bypass pouch revision photo">
      <ImageLightbox fullSrc={bypassResectionEn.url} fullWidth={1200} fullHeight={866} alt="Laparoscopic gastric bypass pouch revision showing the large excess pouch being resected and the new smaller pouch with a sizer bougie inside" caption="This patient's gastric bypass pouch dilated after two term pregnancies. Dr. Fitzer performed a laparoscopic revision, resecting the excess pouch and creating a new, smaller pouch sized with a bougie.">
      <picture>
        <source
          type="image/webp"
          srcSet={`${bypassResectionEn480.url} 480w, ${bypassResectionEn800.url} 800w, ${bypassResectionEn1200.url} 1200w`}
          sizes="(min-width: 1024px) 800px, 100vw"
        />
        <img
          src={bypassResectionEn.url}
          sizes="(min-width: 1024px) 800px, 100vw"
          width={1200}
          height={866}
          alt="Laparoscopic gastric bypass pouch revision showing the large excess pouch being resected and the new smaller pouch with a sizer bougie inside"
          aria-describedby="gastric-bypass-revision-caption"
          className="w-full rounded-lg bg-muted"
          loading="lazy"
          fetchPriority="low"
          decoding="async"
        />
      </picture>
      </ImageLightbox>
      <figcaption
        id="gastric-bypass-revision-caption"
        className="mt-2 text-center text-sm text-foreground/70"
      >
        This patient&rsquo;s gastric bypass pouch dilated after two term pregnancies. Dr. Fitzer
        performed a laparoscopic revision, resecting the excess pouch and creating a new, smaller
        pouch sized with a bougie.
      </figcaption>
    </figure>
  );
}

function LapBandRemovalFigure() {
  return (
    <figure className="mx-auto mt-6 md:max-w-[70%]" aria-label="LAP-BAND removed by Dr. Fitzer">
      <picture>
        <source
          type="image/webp"
          srcSet={`${lapBandRemovedWebp800.url} 800w, ${lapBandRemovedWebp1200.url} 1200w, ${lapBandRemovedWebp1600.url} 1600w`}
          sizes="(min-width: 768px) 300px, calc(100vw - 3rem)"
        />
        <img
          src={lapBandRemovedJpg800.url}
          srcSet={`${lapBandRemovedJpg800.url} 800w, ${lapBandRemovedJpg1200.url} 1200w, ${lapBandRemovedJpg1600.url} 1600w`}
          sizes="(min-width: 768px) 300px, calc(100vw - 3rem)"
          width={1600}
          height={898}
          alt="A LAP-BAND removed by Dr. Fitzer and separated into four pieces"
          aria-describedby="lap-band-removal-caption"
          className="w-full rounded-lg bg-muted bg-cover bg-center"
          style={{
            backgroundImage:
              "url(data:image/webp;base64,UklGRnoAAABXRUJQVlA4IG4AAAAwBACdASoUAAsAPu1iqU2ppaOiMAgBMB2JaACdMoADQZB98On6nYtnQgAA/t1xx4BuxG5H4PHAJvoIb65a7Kh6lE6bwgqaW7dl3xPA7uKT28AG4T9OXbSMvDqP6Aox6I25ClMmMilhrQlGVAAAAA==)",
          }}
          loading="lazy"
          fetchPriority="low"
          decoding="async"
        />
      </picture>
      <figcaption
        id="lap-band-removal-caption"
        className="mt-2 text-center text-sm text-foreground/70"
      >
        A LAP-BAND removed by Dr. Fitzer, in four pieces.
      </figcaption>
    </figure>
  );
}

function BandTubingFigure() {
  return (
    <figure className="mx-auto mt-6 md:max-w-[70%]" aria-label="Band tubing causing intestinal obstruction">
      <picture>
        <source
          type="image/webp"
          srcSet={`${bandTubingWebp800.url} 800w, ${bandTubingWebp1200.url} 1200w, ${bandTubingWebp1600.url} 1600w`}
          sizes="(min-width: 768px) 300px, calc(100vw - 3rem)"
        />
        <img
          src={bandTubingJpg800.url}
          srcSet={`${bandTubingJpg800.url} 800w, ${bandTubingJpg1200.url} 1200w, ${bandTubingJpg1600.url} 1600w`}
          sizes="(min-width: 768px) 300px, calc(100vw - 3rem)"
          width={1600}
          height={899}
          alt="Laparoscopic view of band tubing under tension, hammocked around the root of the small intestine, in a patient with chronic intestinal obstruction"
          aria-describedby="band-tubing-caption"
          className="w-full rounded-lg bg-muted bg-cover bg-center"
          style={{
            backgroundImage:
              "url(data:image/webp;base64,UklGRnwAAABXRUJQVlA4IHAAAADQAwCdASoUAAsAPu1iqU2ppaQiMAgBMB2JagCdMoACBsc47/lUxrgA/tGfRcADG7Smm3BBzLmDobWcOzx2QmOhGSMLQhyGZrDIe8vqbH4fMFmcXnHWWlXwQdwi6qTbxVRd/h3zjcEkXFjX92dL5AAA)",
          }}
          loading="lazy"
          fetchPriority="low"
          decoding="async"
        />
      </picture>
      <figcaption
        id="band-tubing-caption"
        className="mt-2 text-center text-sm text-foreground/70"
      >
        Dr. Fitzer prepares to remove a band in a patient with chronic intestinal
        obstruction caused by the tubing. The tubing is under tension because it
        had become hammocked around the root of the small intestine.
      </figcaption>
    </figure>
  );
}

const POUCH_PAIR_SIZES = "(min-width: 1024px) 320px, (min-width: 640px) 370px, calc((100vw - 4rem) / 3)";
const REVISION_PAIR_SIZES = "(min-width: 640px) 370px, calc((100vw - 4rem) / 2)";

/**
 * Contrast studies of a dilated bypass pouch, followed by a normal comparison.
 */
function DilatedPouchFigurePair() {
  return (
    <section aria-label="Comparison of dilated and normal gastric bypass pouches" className="relative left-1/2 mt-6 grid w-[calc(100vw-3rem)] max-w-[960px] -translate-x-1/2 grid-cols-3 items-start gap-3 md:gap-4">
      <figure className="m-0" aria-label="Upper GI contrast study of a dilated gastric bypass pouch">
        <ImageLightbox fullSrc={pouchContrastJpg980.url} fullWidth={980} fullHeight={957} alt="Upper GI contrast study of a dilated gastric bypass pouch filled with gas, with oral contrast mostly passed into the small intestine" caption="Stretched pouch in a gastric bypass patient twelve years and two term pregnancies after the initial surgery.">
        <picture>
          <source
            type="image/webp"
            srcSet={`${pouchContrastWebp800.url} 800w, ${pouchContrastWebp980.url} 980w`}
            sizes={POUCH_PAIR_SIZES}
          />
          <img
            src={pouchContrastJpg800.url}
            srcSet={`${pouchContrastJpg800.url} 800w, ${pouchContrastJpg980.url} 980w`}
            sizes={POUCH_PAIR_SIZES}
            width={980}
            height={957}
            alt="Upper GI contrast study of a dilated gastric bypass pouch filled with gas, with oral contrast mostly passed into the small intestine, twelve years after the original surgery"
            aria-describedby="dilated-pouch-contrast-caption"
            className="w-full rounded-lg bg-muted bg-cover bg-center"
            style={{
              backgroundImage:
                "url(data:image/webp;base64,UklGRnIAAABXRUJQVlA4IGYAAAAwBACdASoUABQAPu1wr1KppiQiqAgBMB2JaQAALnRe1ObYWPJuwLa1nQAA9ruzQDDcyT62KAiQE4jtd2pfKB3mRu6/9T4++VNsUldfmNvUrnWeTd5/ZEVYugHEvnNiQeMQE7oAAAA=)",
            }}
            loading="lazy"
            fetchPriority="low"
            decoding="async"
          />
        </picture>
        </ImageLightbox>
        <figcaption
          id="dilated-pouch-contrast-caption"
          className="mt-1 line-clamp-2 text-center text-xs text-foreground/70 md:mt-2 md:line-clamp-none md:text-sm"
        >
          Stretched pouch in a gastric bypass patient twelve years and two term pregnancies
          after the initial surgery. The oral contrast has mostly flowed into the small
          intestine, leaving the pouch filled with CO<sub>2</sub> (gas) released by the
          effervescent crystals swallowed as part of the test.
        </figcaption>
      </figure>
      <figure className="m-0" aria-label="Annotated upper GI contrast study of the same dilated pouch">
        <ImageLightbox fullSrc={pouchAnnotatedJpg980.url} fullWidth={980} fullHeight={957} alt="Upper GI contrast study annotated by Dr. Fitzer with blue arrows for the esophagus and small intestine and a blue dashed outline of the dilated gastric bypass pouch" caption="Same pouch, now with annotation by Dr. Fitzer. The pouch is outlined in blue.">
        <picture>
          <source
            type="image/webp"
            srcSet={`${pouchAnnotatedWebp480.url} 480w, ${pouchAnnotatedWebp800.url} 800w, ${pouchAnnotatedWebp980.url} 980w`}
            sizes={POUCH_PAIR_SIZES}
          />
          <img
            src={pouchAnnotatedJpg800.url}
            srcSet={`${pouchAnnotatedJpg480.url} 480w, ${pouchAnnotatedJpg800.url} 800w, ${pouchAnnotatedJpg980.url} 980w`}
            sizes={POUCH_PAIR_SIZES}
            width={980}
            height={957}
            alt="Upper GI contrast study annotated by Dr. Fitzer with blue arrows for the esophagus and small intestine and a blue dashed outline of the dilated gastric bypass pouch"
            aria-describedby="dilated-pouch-annotated-caption"
            className="w-full rounded-lg bg-muted bg-cover bg-center"
            style={{
              backgroundImage:
                "url(data:image/webp;base64,UklGRn4AAABXRUJQVlA4IHIAAABQBACdASoUABQAPt1orVEopaQiqAgBEBuJZQAALnRkByubCYIvLSg99eOgAPa7s9WzLjhuughf/36kIvQUKYZv5o4qp+Dyy5ptdBj+BnnnzDt3xVa2N402F1UVqK+Ng+5ezieSlWvpS/yjGYNSg6QAAAA=)",
            }}
            loading="lazy"
            fetchPriority="low"
            decoding="async"
          />
        </picture>
        </ImageLightbox>
        <figcaption
          id="dilated-pouch-annotated-caption"
          className="mt-1 line-clamp-2 text-center text-xs text-foreground/70 md:mt-2 md:line-clamp-none md:text-sm"
        >
          Same pouch, now with annotation by Dr. Fitzer. The pouch is outlined in blue. A normal
          pouch is about the same width as the esophagus, so this pouch is clearly larger than it
          should be. She experienced return of proper weight loss after revision.
        </figcaption>
      </figure>
      <ProcedureFigure media="revisionPouchNormalEn" lang="en" lightbox sizes={POUCH_PAIR_SIZES} imgClassName="h-auto w-full rounded-lg bg-muted" captionClassName="mt-1 line-clamp-2 text-center text-xs text-foreground/70 md:mt-2 md:line-clamp-none md:text-sm" />
    </section>
  );
}

function SleeveRevisionFigurePair() {
  return (
    <section
      aria-label="Laparoscopic comparison of a normal sleeve and a stretched sleeve"
      className="mt-6 grid grid-cols-2 items-start gap-3 md:gap-4"
    >
      <ProcedureFigure
        media="sleeveNormalRevision"
        lightbox
        sizes={REVISION_PAIR_SIZES}
        imgClassName="h-auto w-full rounded-lg bg-muted"
        captionClassName="mt-1 line-clamp-2 text-center text-xs text-foreground/70 md:mt-2 md:line-clamp-none md:text-sm"
      />
      <ProcedureFigure
        media="sleeveStretchedRevision"
        lightbox
        sizes={REVISION_PAIR_SIZES}
        imgClassName="h-auto w-full rounded-lg bg-muted"
        captionClassName="mt-1 line-clamp-2 text-center text-xs text-foreground/70 md:mt-2 md:line-clamp-none md:text-sm"
      />
    </section>
  );
}

function SleeveUgiFigurePair() {
  return (
    <section
      aria-label="Upper GI series comparing a normal sleeve and a dilated sleeve"
      className="mt-6 grid grid-cols-2 items-start gap-3 md:gap-4"
    >
      <ProcedureFigure
        media="sleeveUgiNormalEn"
        lang="en"
        lightbox
        previewCropTopPercent={25}
        previewCropBottomPercent={8}
        sizes={REVISION_PAIR_SIZES}
        imgClassName="h-auto w-full rounded-lg bg-muted"
        captionClassName="mt-1 line-clamp-2 text-center text-xs text-foreground/70 md:mt-2 md:line-clamp-none md:text-sm"
      />
      <ProcedureFigure
        media="sleeveUgiDilatedEn"
        lang="en"
        lightbox
        sizes={REVISION_PAIR_SIZES}
        imgClassName="h-auto w-full rounded-lg bg-muted"
        captionClassName="mt-1 line-clamp-2 text-center text-xs text-foreground/70 md:mt-2 md:line-clamp-none md:text-sm"
      />
    </section>
  );
}

/** Before/after UGI pair for the reflux section: hernia + reflux, then post-repair. */
function SleeveRefluxFigurePair() {
  return (
    <section
      aria-label="Upper GI series before and after hiatal hernia repair with conversion to bypass"
      className="mt-6 grid grid-cols-2 items-start gap-3 md:gap-4"
    >
      <ProcedureFigure
        media="sleeveUgiRefluxPreEn"
        lang="en"
        lightbox
        sizes={REVISION_PAIR_SIZES}
        previewCropTopPercent={8}
        previewCropBottomPercent={4}
        imgClassName="h-auto w-full rounded-lg bg-muted"
        captionClassName="mt-1 line-clamp-2 text-center text-xs text-foreground/70 md:mt-2 md:line-clamp-none md:text-sm"
      />
      <ProcedureFigure
        media="sleeveUgiRefluxPostEn"
        lang="en"
        lightbox
        sizes={REVISION_PAIR_SIZES}
        imgClassName="h-auto w-full rounded-lg bg-muted"
        captionClassName="mt-1 line-clamp-2 text-center text-xs text-foreground/70 md:mt-2 md:line-clamp-none md:text-sm"
      />
    </section>
  );
}



/**
 * Shared layout for the three revision spokes. Body prose is driven entirely
 * by the spoke record in src/lib/revision-spokes.ts, so swapping placeholder
 * sections for the practice's final copy is a data-only change.
 */
export function RevisionSpokePage({ spoke }: { spoke: RevisionSpoke }) {
  return (
    <article className="mx-auto max-w-3xl px-6 md:px-12 lg:px-32 xl:px-44 2xl:px-56 pt-4 pb-10 md:text-[1.125rem] leading-[1.6]">
      <header>
        <nav aria-label="Breadcrumb" className="text-sm">
          <Link to="/services/revisions" preload="intent" className="text-brand-deep underline hover:no-underline">
            Revision bariatric surgery
          </Link>
        </nav>
        <h1 className="mt-2 text-[2.025rem] md:text-[2.7rem] font-serif text-center">{spoke.h1}</h1>
        <p className="mt-1 text-foreground/70 italic">{spoke.kicker}</p>
        <nav aria-label="On this page" className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
          {spoke.sections.map((section) => (
            <a key={section.id} href={`#${section.id}`} className="text-brand-deep underline hover:no-underline">
              {section.heading}
            </a>
          ))}
          <a href="#faq" className="text-brand-deep underline hover:no-underline">
            FAQs
          </a>
        </nav>
      </header>

      <MedicalReviewBanner lang="en" className="mt-4" />

      {spoke.intro.map((paragraph) => (
        <p key={paragraph.slice(0, 40)} className="mt-3 text-foreground/80">
          {paragraph}
        </p>
      ))}

      <TelehealthCta />

      {spoke.sections.map((section, index) => (
        <section
          key={section.id}
          aria-labelledby={`${section.id}-heading`}
          className={index === 0 ? undefined : "defer-render"}
        >

          <h2
            id={section.id}
            className="mt-6 scroll-mt-24 text-[1.35rem] md:text-[1.6875rem] font-serif"
          >
            <span id={`${section.id}-heading`}>{section.heading}</span>
          </h2>
          {section.paragraphs.map((paragraph, pIndex) => (
            <Fragment key={paragraph.slice(0, 40)}>
              <p className="mt-3 text-foreground/80">{renderInlineLinks(paragraph)}</p>
              {section.bullets && section.bulletsAfterParagraph === pIndex ? (
                <ul className="mt-2 list-disc space-y-1 pl-6 text-foreground/80">
                  {section.bullets.map((bullet) => <li key={bullet}>{renderInlineLinks(bullet)}</li>)}
                </ul>
              ) : null}
              {spoke.slug === "gastric-bypass" &&
              section.id === "choosing-between-endoscopic-and-surgical-revision" &&
              pIndex === 2 ? (
                <BypassRevisionFigure />
              ) : null}
              {spoke.slug === "gastric-bypass" &&
              section.id === "why-weight-returns-after-bypass" &&
              pIndex === 0 ? (
                <DilatedPouchFigurePair />
              ) : null}
              {spoke.slug === "sleeve" &&
              section.id === "why-sleeves-are-revised" &&
              pIndex === 1 ? (
                <SleeveRevisionFigurePair />
              ) : null}
              {spoke.slug === "sleeve" && section.id === "re-sleeve" && pIndex === 1 ? (
                <SleeveUgiFigurePair />
              ) : null}
              {spoke.slug === "sleeve" && section.id === "reflux" && pIndex === 1 ? (
                <SleeveRefluxFigurePair />
              ) : null}
              {spoke.slug === "lap-band" && section.id === "why-bands-are-removed" && pIndex === 0 ? (
                <BandTubingFigure />
              ) : null}
              {spoke.slug === "lap-band" && section.id === "warning-signs" && pIndex === 1 ? (
                <ProcedureFigure
                  media="bandDilatedEsophagus"
                  lightbox
                  className="mx-auto mt-6 md:max-w-[70%]"
                  sizes="(min-width: 768px) 300px, calc(100vw - 3rem)"
                  imgClassName="h-auto w-full rounded-lg bg-muted"
                  captionClassName="mt-1 line-clamp-2 text-center text-xs text-foreground/70 md:mt-2 md:line-clamp-none md:text-sm"
                />
              ) : null}
              {spoke.slug === "lap-band" && section.id === "removal-alone" && pIndex === 0 ? (
                <LapBandRemovalFigure />
              ) : null}
              {section.gallery && section.galleryAfterParagraph === pIndex ? (
                <GallerySection gallery={section.gallery} />
              ) : null}
              {spoke.slug === "sleeve" &&
              section.id === "sleeve-to-bypass" &&
              section.gallery &&
              section.galleryAfterParagraph === pIndex ? (
                <SectionCta to="/appointments" label="cta_sleeve_revision_after_bypass_gallery">
                  Talk About Your Options with Dr. Fitzer
                </SectionCta>
              ) : null}
            </Fragment>
          ))}
          {section.bullets && (section.bulletsAfterParagraph === undefined || section.bulletsAfterParagraph === -1) ? (
            <ul className="mt-2 list-disc space-y-1 pl-6 text-foreground/80">
              {section.bullets.map((bullet) => (
                <li key={bullet}>{renderInlineLinks(bullet)}</li>
              ))}
            </ul>
          ) : null}
          {section.gallery && section.galleryAfterParagraph === undefined ? (
            <GallerySection gallery={section.gallery} />
          ) : null}
          {spoke.slug === "sleeve" && section.id === "sadi-duodenal-switch" ? (
            <SectionCta to="/appointments" label="cta_sleeve_revision_after_options">Find Out Which Revision Is Right for You</SectionCta>
          ) : spoke.slug === "sleeve" && section.id === "expected-results" ? (
            <SectionCta to="/resources/qualifications" label="cta_sleeve_revision_after_results">See if You Qualify for Revision</SectionCta>
          ) : spoke.slug === "sleeve" && section.id === "insurance" ? (
            <SectionCta to="/resources/insurance-coverage" label="cta_sleeve_revision_after_insurance">Check Your Revision Coverage</SectionCta>
          ) : null}
        </section>
      ))}


      <h2 id="faq" className="mt-6 scroll-mt-24 text-[1.35rem] md:text-[1.6875rem] font-serif">
        {spoke.faqHeading ?? "Frequently asked questions"}
      </h2>
      <dl className="defer-render mt-2 space-y-3 text-foreground/80">
        {spoke.faqs.map((faq) => (
          <div key={faq.question}>
            <dt className="font-medium text-foreground">{faq.question}</dt>
            <dd className="mt-1">
              {faq.answer.split(/\n{2,}/).map((para, i) => (
                <p key={i} className={i > 0 ? "mt-3" : undefined}>{para}</p>
              ))}
            </dd>
          </div>
        ))}
      </dl>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Related pages</h2>
      <ul className="mt-2 list-disc space-y-1 pl-6 text-foreground/80">
        {spoke.relatedPages ? spoke.relatedPages.map((page) => (
          <li key={page.to}>
            <Link to={page.to} preload="intent" className="text-brand-deep underline hover:no-underline">{page.label}</Link>
          </li>
        )) : <>
        <li>
          <Link to="/services/revisions" preload="intent" className="text-brand-deep underline hover:no-underline">
            Revision bariatric surgery overview
          </Link>{" "}
          — how revision care works and who is a candidate.
        </li>
        <li>
          <Link to={spoke.sibling.to} preload="intent" className="text-brand-deep underline hover:no-underline">
            {spoke.sibling.label}
          </Link>{" "}
          — the closest alternative if this is not your original operation.
        </li>
        <li>
          <Link to={spoke.relatedProcedure.to} preload="intent" className="text-brand-deep underline hover:no-underline">
            {spoke.relatedProcedure.label}
          </Link>{" "}
          — details on the first-time procedure.
        </li>
        <li>
          <Link to="/resources/insurance-coverage" preload="intent" className="text-brand-deep underline hover:no-underline">
            Insurance coverage for bariatric surgery
          </Link>{" "}
          — what plans ask for before approving a revision.
        </li>
        <li>
          <Link to="/self-pay-bariatric-surgery" preload="intent" className="text-brand-deep underline hover:no-underline">
            Self-pay bariatric surgery
          </Link>{" "}
          — published prices if you are not using insurance.
        </li>
        </>}
      </ul>

      {spoke.closing || spoke.slug === "tore" ? (
        <>
          <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Take the next step</h2>
          {spoke.closing ? <p className="mt-3 text-foreground/80">{spoke.closing}</p> : null}
        </>
      ) : null}

      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link
          to="/appointments"
          className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          Request a consultation
        </Link>
        <WeightLossOptionsButtons />
      </div>

      {spoke.byline ? (
        <p className="mt-6 text-sm italic text-foreground/70">{spoke.byline}</p>
      ) : null}

      {spoke.references?.length ? (
        <section aria-labelledby="medical-references" className="defer-render">
          <h2
            id="medical-references"
            className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif"
          >
            Medical references
          </h2>
          <ol className="mt-2 list-decimal space-y-2 pl-6 text-sm text-foreground/70">
            {spoke.references.map((reference) => (
              <li key={reference.slice(0, 40)}>{reference}</li>
            ))}
          </ol>
        </section>
      ) : null}
      <LastUpdated className="mt-10" />
    </article>
  );
}

